import { Fragment, type ReactNode } from 'react'

type MarkdownBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; level: 2 | 3 | 4; text: string }
  | { type: 'unordered-list'; items: string[] }
  | { type: 'ordered-list'; items: string[] }
  | { type: 'quote'; text: string }
  | { type: 'image'; alt: string; src: string }
  | { type: 'rule' }

function isSafeHref(href: string) {
  return /^(https?:\/\/|mailto:|\/)/i.test(href)
}

function renderInline(text: string): ReactNode {
  const tokenPattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g
  const chunks = text.split(tokenPattern).filter(Boolean)

  return chunks.map((chunk, index) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return <strong key={index}>{chunk.slice(2, -2)}</strong>
    }
    if (chunk.startsWith('`') && chunk.endsWith('`')) {
      return <code key={index}>{chunk.slice(1, -1)}</code>
    }
    if (chunk.startsWith('*') && chunk.endsWith('*')) {
      return <em key={index}>{chunk.slice(1, -1)}</em>
    }

    const link = chunk.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (link) {
      const [, label, href] = link
      if (!isSafeHref(href)) return <Fragment key={index}>{label}</Fragment>
      const external = href.startsWith('http')
      return (
        <a
          key={index}
          href={href}
          className="article-link"
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {label}
        </a>
      )
    }

    return <Fragment key={index}>{chunk}</Fragment>
  })
}

function parseMarkdown(content: string): MarkdownBlock[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: MarkdownBlock[] = []
  let paragraph: string[] = []
  let unordered: string[] = []
  let ordered: string[] = []

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
      paragraph = []
    }
  }
  const flushLists = () => {
    if (unordered.length) {
      blocks.push({ type: 'unordered-list', items: unordered })
      unordered = []
    }
    if (ordered.length) {
      blocks.push({ type: 'ordered-list', items: ordered })
      ordered = []
    }
  }
  const flushAll = () => {
    flushParagraph()
    flushLists()
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()

    if (!line) {
      flushAll()
      continue
    }

    const image = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/)
    if (image && isSafeHref(image[2])) {
      flushAll()
      blocks.push({ type: 'image', alt: image[1], src: image[2] })
      continue
    }

    const heading = line.match(/^(#{2,4})\s+(.+)$/)
    if (heading) {
      flushAll()
      blocks.push({
        type: 'heading',
        level: heading[1].length as 2 | 3 | 4,
        text: heading[2],
      })
      continue
    }

    if (/^(-{3,}|\*{3,})$/.test(line)) {
      flushAll()
      blocks.push({ type: 'rule' })
      continue
    }

    if (line.startsWith('> ')) {
      flushAll()
      blocks.push({ type: 'quote', text: line.slice(2) })
      continue
    }

    const unorderedItem = line.match(/^[-*]\s+(.+)$/)
    if (unorderedItem) {
      flushParagraph()
      if (ordered.length) flushLists()
      unordered.push(unorderedItem[1])
      continue
    }

    const orderedItem = line.match(/^\d+[.)]\s+(.+)$/)
    if (orderedItem) {
      flushParagraph()
      if (unordered.length) flushLists()
      ordered.push(orderedItem[1])
      continue
    }

    flushLists()
    paragraph.push(line)
  }

  flushAll()
  return blocks
}

export default function BlogPostBody({ content }: { content: string }) {
  const blocks = parseMarkdown(content)

  return (
    <div className="article-body blog-article-body">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading': {
            if (block.level === 2) return <h2 key={index} className="article-h3">{renderInline(block.text)}</h2>
            if (block.level === 3) return <h3 key={index} className="article-h4">{renderInline(block.text)}</h3>
            return <h4 key={index} className="article-h5">{renderInline(block.text)}</h4>
          }
          case 'unordered-list':
            return (
              <ul key={index} className="article-ul">
                {block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}
              </ul>
            )
          case 'ordered-list':
            return (
              <ol key={index} className="article-ol">
                {block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}
              </ol>
            )
          case 'quote':
            return <blockquote key={index} className="blog-article-quote">{renderInline(block.text)}</blockquote>
          case 'image':
            return (
              <figure key={index} className="article-figure">
                <img src={block.src} alt={block.alt} className="article-img" loading="lazy" />
                {block.alt && <figcaption className="blog-article-caption">{block.alt}</figcaption>}
              </figure>
            )
          case 'rule':
            return <hr key={index} className="blog-article-rule" />
          default:
            return <p key={index} className="article-p">{renderInline(block.text)}</p>
        }
      })}
    </div>
  )
}
